
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Grid
} from '@mui/material';
import {
  Google as GoogleIcon,
  VideoCall as VideoCallIcon,
  CalendarMonth as CalendarIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  ExitToApp as ExitToAppIcon,
  Transcribe as TranscriptIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
  Summarize as SummarizeIcon
} from '@mui/icons-material';
import { Menu, MenuItem, ListItemIcon } from '@mui/material';
import useAuthState from '../../../contexts/AuthStateContext';
import { createApiRequest } from '../../common';
import { API_BASE_URL } from '../../../constants/api.constants';

interface OAuthConnection {
  provider: string;
  connected: boolean;
  scopes: string[];
}

interface CalendarEvent {
  id: string;
  title: string;
  description?: string;
  startTime: string;
  endTime: string;
  meetingUrl?: string;
  attendees: Array<{
    email: string;
    name?: string;
    status?: string;
  }>;
  isRecurring: boolean;
  provider: string;
}

interface MeetingBot {
  botId: string;
  status: string;
  meetingUrl: string;
  botName?: string;
  createdAt: string;
  joinAt?: string;
}

// Valid Recall.ai bot statuses
const ACTIVE_CALL_STATUSES = [
  'in_waiting_room',
  'in_call_not_recording',
  'recording_permission_allowed',
  'recording_permission_denied',
  'in_call_recording'
];

const PRE_CALL_STATUSES = ['ready', 'joining_call'];

const TERMINAL_STATUSES = [
  'recording_done',
  'call_ended',
  'done',
  'fatal',
  'media_expired',
  'analysis_done',
  'analysis_failed'
];

/**
 * Test component for Meeting Bot with OAuth and Calendar integration
 */
const TestMeetingBot: React.FC = () => {
  const { profile } = useAuthState();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // OAuth state
  const [connections, setConnections] = useState<OAuthConnection[]>([]);

  // Calendar state
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  // Manual bot state
  const [meetingUrl, setMeetingUrl] = useState('');
  const [botName, setBotName] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [bots, setBots] = useState<MeetingBot[]>([]);

  // Dialog state
  const [createBotDialogOpen, setCreateBotDialogOpen] = useState(false);

  // More options menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedBotForMenu, setSelectedBotForMenu] = useState<MeetingBot | null>(null);

  // States for recurring event dialog
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [selectedRecurringEvent, setSelectedRecurringEvent] = useState<CalendarEvent | null>(null);

  // States for edit dialog
  const [editBotDialogOpen, setEditBotDialogOpen] = useState(false);
  const [editingBot, setEditingBot] = useState<MeetingBot | null>(null);

  /**
   * Task 18: OAuth Connection Management
   */

  // Load OAuth connections on mount
  useEffect(() => {
    loadConnections();
  }, []);

  // Load bots when switching to Meeting Bots tab
  useEffect(() => {
    if (activeTab === 2) {
      loadBots();
    }
  }, [activeTab]);

  const loadConnections = async () => {
    /* Implementation:
     * 1. Call /oauth/connections endpoint
     * 2. Update connections state
     * 3. Handle errors
     *
     * Example:
     * try {
     *   const token = await getAccessToken();
     *   const response = await apiCall('/oauth/connections', 'POST', {}, token);
     *   setConnections(response);
     * } catch (err) {
     *   setError('Failed to load OAuth connections');
     * }
     */
    try {
      const request = createApiRequest<OAuthConnection[]>('post', '/oauth/connections');
      const response = await request({});
      setConnections(response);
    } catch (err) {
      setError('Failed to load OAuth connections');
    }
  };

  const connectOAuth = (provider: string) => {
    /* Implementation:
     * 1. Open popup window for OAuth flow
     * 2. Pass userId as query parameter
     * 3. Listen for success/error messages
     * 4. Reload connections on success
     *
     * Example:
     * const width = 500;
     * const height = 600;
     * const left = window.screenX + (window.outerWidth - width) / 2;
     * const top = window.screenY + (window.outerHeight - height) / 2;
     *
     * const authUrl = `${process.env.REACT_APP_API_URL}/oauth/${provider}/authorize?userId=${user?.id}`;
     * const popup = window.open(
     *   authUrl,
     *   `oauth-${provider}`,
     *   `width=${width},height=${height},left=${left},top=${top}`
     * );
     *
     * // Listen for OAuth result
     * const handleMessage = (event: MessageEvent) => {
     *   if (event.data.type === 'oauth-success' && event.data.provider === provider) {
     *     setSuccess(`Successfully connected ${provider}`);
     *     loadConnections();
     *     popup?.close();
     *   } else if (event.data.type === 'oauth-error' && event.data.provider === provider) {
     *     setError(`Failed to connect ${provider}: ${event.data.error}`);
     *     popup?.close();
     *   }
     * };
     *
     * window.addEventListener('message', handleMessage);
     * return () => window.removeEventListener('message', handleMessage);
     */
    const width = 500;
    const height = 600;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const authUrl = `${API_BASE_URL}/oauth/${provider}/authorize?userId=${profile?.userId}`;
    const popup = window.open(
      authUrl,
      `oauth-${provider}`,
      `width=${width},height=${height},left=${left},top=${top}`
    );

    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'oauth-success' && event.data.provider === provider) {
        setSuccess(`Successfully connected ${provider}`);
        loadConnections();
        loadCalendarEvents(); // Load events after connection
        popup?.close();
      } else if (event.data.type === 'oauth-error' && event.data.provider === provider) {
        setError(`Failed to connect ${provider}: ${event.data.error}`);
        popup?.close();
      }
    };

    window.addEventListener('message', handleMessage);
  };

  const disconnectOAuth = async (provider: string) => {
    /* Implementation:
     * 1. Call /oauth/disconnect endpoint
     * 2. Reload connections
     * 3. Clear calendar events if disconnected
     *
     * Example:
     * try {
     *   setLoading(true);
     *   const token = await getAccessToken();
     *   await apiCall('/oauth/disconnect', 'POST', { provider }, token);
     *   setSuccess(`Disconnected ${provider}`);
     *   loadConnections();
     *   setCalendarEvents([]);
     * } catch (err) {
     *   setError(`Failed to disconnect ${provider}`);
     * } finally {
     *   setLoading(false);
     * }
     */
    try {
      setLoading(true);
      const request = createApiRequest('post', '/oauth/disconnect');
      await request({ provider });
      setSuccess(`Disconnected ${provider}`);
      loadConnections();
      setCalendarEvents([]);
    } catch (err) {
      setError(`Failed to disconnect ${provider}`);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Task 19: Calendar Events Display
   */

  const loadCalendarEvents = async () => {
    /* Implementation:
     * 1. Call /calendar/events endpoint
     * 2. Pass time range (next 7 days)
     * 3. Update events state
     * 4. Filter for events with meeting URLs
     *
     * Example:
     * try {
     *   setLoading(true);
     *   const token = await getAccessToken();
     *   const now = new Date();
     *   const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
     *
     *   const response = await apiCall('/calendar/events', 'POST', {
     *     timeMin: now.toISOString(),
     *     timeMax: nextWeek.toISOString(),
     *     maxResults: 50
     *   }, token);
     *
     *   setCalendarEvents(response);
     * } catch (err) {
     *   setError('Failed to load calendar events');
     * } finally {
     *   setLoading(false);
     * }
     */
    try {
      setLoading(true);
      const now = new Date();
      const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

      const request = createApiRequest<CalendarEvent[]>('post', '/calendar/events');
      const response = await request({
        timeMin: now.toISOString(),
        timeMax: nextWeek.toISOString(),
        maxResults: 50
      });

      setCalendarEvents(response);
    } catch (err) {
      setError('Failed to load calendar events');
    } finally {
      setLoading(false);
    }
  };

  // Handle clicking create bot for an event
  const handleCreateBotFromEvent = (event: CalendarEvent) => {
    if (event.isRecurring) {
      setSelectedRecurringEvent(event);
      setRecurringDialogOpen(true);
    } else {
      createBotFromEvent(event, false);
    }
  };

  // Create bot from calendar event with optional next instance scheduling
  const createBotFromEvent = async (event: CalendarEvent, scheduleNext: boolean = false) => {
    try {
      setLoading(true);
      const request = createApiRequest<any>('post', '/calendar/create-bot');
      const response = await request({
        eventId: event.id,
        botName: event.title,
        scheduleNextRecurrence: scheduleNext
      });

      if (scheduleNext && response.nextBotScheduled) {
        setSuccess(`Bot created for ${event.title} and next occurrence scheduled`);
      } else {
        setSuccess(`Bot created for ${event.title}`);
      }
      loadBots();
      setRecurringDialogOpen(false);
    } catch (err) {
      setError('Failed to create bot from event');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Task 20: Manual Bot Creation
   */

  const createManualBot = async () => {
    try {
      setLoading(true);

      // Validate scheduled time if provided (must be at least 10 minutes in future)
      let joinAt: string | undefined = undefined;
      if (scheduledTime) {
        const scheduledDate = new Date(scheduledTime);
        const minFutureTime = new Date(Date.now() + 10 * 60 * 1000); // 10 minutes

        if (scheduledDate < minFutureTime) {
          setError('Scheduled time must be at least 10 minutes in the future');
          setLoading(false);
          return;
        }

        joinAt = scheduledDate.toISOString();
      }

      const request = createApiRequest<MeetingBot>('post', '/meeting-bot/create');
      const response = await request({
        meetingUrl,
        botName: botName || undefined,
        joinAt
      });

      const statusMsg = joinAt
        ? `Scheduled bot created with ID: ${response.botId} for ${new Date(joinAt).toLocaleString()}`
        : `Bot created with ID: ${response.botId}`;

      setSuccess(statusMsg);
      setMeetingUrl('');
      setBotName('');
      setScheduledTime('');
      loadBots();
      setCreateBotDialogOpen(false);
    } catch (err) {
      setError('Failed to create bot');
    } finally {
      setLoading(false);
    }
  };

  const loadBots = async () => {
    /* Implementation:
     * 1. Call /meeting-bot/list endpoint
     * 2. Update bots state
     * 3. Handle errors
     *
     * Example:
     * try {
     *   const token = await getAccessToken();
     *   const response = await apiCall('/meeting-bot/list', 'POST', { limit: 10 }, token);
     *   setBots(response.bots || []);
     * } catch (err) {
     *   console.error('Failed to load bots', err);
     * }
     */
    try {
      const request = createApiRequest<{ bots: MeetingBot[] }>('post', '/meeting-bot/list');
      const response = await request({ limit: 10 });
      setBots(response.bots || []);
    } catch (err) {
      console.error('Failed to load bots', err);
    }
  };

  const cancelBot = async (botId: string) => {
    /* Implementation:
     * 1. Call /meeting-bot/cancel endpoint
     * 2. Show success message
     * 3. Refresh bot list
     */
    try {
      setLoading(true);
      const request = createApiRequest('post', '/meeting-bot/cancel');
      await request({ botId });
      setSuccess('Bot cancelled');
      loadBots();
    } catch (err) {
      setError('Failed to cancel bot');
    } finally {
      setLoading(false);
    }
  };

  // Edit bot functionality - uses cancel + create
  const handleEditBot = (bot: MeetingBot) => {
    setEditingBot(bot);
    setMeetingUrl(bot.meetingUrl);
    setBotName(bot.botName || '');
    setEditBotDialogOpen(true);
  };

  const saveEditedBot = async () => {
    try {
      setLoading(true);
      // STEP 1: Cancel the old bot using existing endpoint
      await cancelBot(editingBot!.botId);

      // STEP 2: Create new bot with updated details using existing endpoint
      const request = createApiRequest<MeetingBot>('post', '/meeting-bot/create');
      await request({
        meetingUrl,
        botName: botName || undefined,
        joinAt: editingBot!.joinAt
      });

      setSuccess('Bot updated successfully');
      setEditBotDialogOpen(false);
      loadBots();
    } catch (err) {
      setError('Failed to update bot');
    } finally {
      setLoading(false);
    }
  };

  // Delete bot functionality
  const handleDeleteBot = async (botId: string) => {
    if (!confirm('Are you sure you want to delete this bot?')) return;

    try {
      setLoading(true);
      // Use cancel endpoint for all cases (same underlying API)
      await cancelBot(botId);
      setSuccess('Bot deleted successfully');
      loadBots();
    } catch (err) {
      setError('Failed to delete bot');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveCall = async (botId: string) => {
    try {
      setLoading(true);
      const request = createApiRequest('post', '/meeting-bot/leave-call');
      await request({ botId });
      setSuccess('Bot left the call');
      loadBots();
    } catch (err) {
      setError('Failed to make bot leave call');
    } finally {
      setLoading(false);
    }
  };

  const viewTranscript = async (botId: string) => {
    try {
      setLoading(true);
      const request = createApiRequest<{ transcript: any }>('post', '/meeting-bot/transcript');
      const response = await request({ botId });

      if (!response.transcript) {
        setError('Transcript not yet available');
        return;
      }

      // Display transcript in a new dialog or console for now
      console.log('Transcript:', response.transcript);

      // Count total words across all participants
      const totalWords = Array.isArray(response.transcript)
        ? response.transcript.reduce((sum: number, entry: any) => sum + (entry.words?.length || 0), 0)
        : 0;

      setSuccess(`Transcript retrieved: ${response.transcript.length} participants, ${totalWords} words. Check console for details.`);
    } catch (err) {
      setError('Failed to retrieve transcript');
    } finally {
      setLoading(false);
    }
  };

  const viewRecording = async (botId: string) => {
    try {
      setLoading(true);
      const request = createApiRequest<{ recordingUrl: string | null }>('post', '/meeting-bot/recording');
      const response = await request({ botId });

      if (!response.recordingUrl) {
        setError('Recording not yet available');
        return;
      }

      // Open recording in new tab
      window.open(response.recordingUrl, '_blank');
      setSuccess('Recording opened in new tab');
    } catch (err) {
      setError('Failed to retrieve recording');
    } finally {
      setLoading(false);
    }
  };

  const refreshBotStatus = async (botId: string) => {
    try {
      const request = createApiRequest<{ status: string }>('post', '/meeting-bot/status');
      const response = await request({ botId });
      setSuccess(`Bot status refreshed: ${response.status}`);
      loadBots();
    } catch (err) {
      setError('Failed to refresh bot status');
    }
  };

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, bot: MeetingBot) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedBotForMenu(bot);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedBotForMenu(null);
  };

  const handleMenuAction = async (action: 'transcript' | 'recording' | 'summary') => {
    if (!selectedBotForMenu) return;

    handleMenuClose();

    switch (action) {
      case 'transcript':
        await viewTranscript(selectedBotForMenu.botId);
        break;
      case 'recording':
        await viewRecording(selectedBotForMenu.botId);
        break;
      case 'summary':
        await viewWorkflowSummary(selectedBotForMenu.botId);
        break;
    }
  };

  const viewWorkflowSummary = async (botId: string) => {
    try {
      setLoading(true);

      const request = createApiRequest<{
        formattedTranscript?: any;
        summary?: any;
        status: string;
      }>('post', '/meeting-bot/workflow-summary');

      const response = await request({ botId });

      if (response.status === 'pending' || response.status === 'not_found') {
        setError(`Transcript workflow not yet complete (status: ${response.status})`);
        return;
      }

      if (response.status === 'failed') {
        setError('Transcript workflow failed');
        return;
      }

      // Log formatted transcript
      if (response.formattedTranscript) {
        console.log('=== FORMATTED TRANSCRIPT ===');
        console.log('Text:', response.formattedTranscript.text);
        console.log('Metadata:', response.formattedTranscript.metadata);
      }

      // Log summary
      if (response.summary) {
        console.log('=== MEETING SUMMARY ===');
        console.log('Title:', response.summary.title);
        console.log('Summary:', response.summary.summary);
        console.log('Key Points:', response.summary.keyPoints);
        console.log('Action Items:', response.summary.actionItems);
        console.log('Decisions:', response.summary.decisions);
        console.log('Topics:', response.summary.topics);
      }

      const summaryText = response.summary
        ? `Retrieved workflow summary: "${response.summary.title}". Check console for full details.`
        : 'Workflow data retrieved. Check console for details.';

      setSuccess(summaryText);
    } catch (err) {
      setError('Failed to retrieve workflow summary');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Meeting Bot & Calendar Integration
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="OAuth Connections" />
        <Tab label="Calendar Events" />
        <Tab label="Meeting Bots" />
      </Tabs>

      {/* OAuth Connections Tab */}
      {activeTab === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              OAuth Provider Connections
            </Typography>
            <List>
              {connections.map((connection) => (
                <ListItem key={connection.provider}>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        {connection.provider === 'google' && <GoogleIcon />}
                        <Typography>{connection.provider.charAt(0).toUpperCase() + connection.provider.slice(1)}</Typography>
                        {connection.connected && <CheckCircleIcon color="success" fontSize="small" />}
                      </Stack>
                    }
                    secondary={
                      connection.connected
                        ? `Connected - Scopes: ${connection.scopes.join(', ')}`
                        : 'Not connected'
                    }
                  />
                  <ListItemSecondaryAction>
                    {connection.connected ? (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => disconnectOAuth(connection.provider)}
                        disabled={loading}
                      >
                        Disconnect
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => connectOAuth(connection.provider)}
                        disabled={loading}
                      >
                        Connect
                      </Button>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Calendar Events Tab */}
      {activeTab === 1 && (
        <Card>
          <CardContent>
            {/* TODO: Future Enhancement - Visual Calendar Widget
            - Replace the current list view with a visual calendar component
            - Options to consider:
              - react-big-calendar: Full-featured calendar with month/week/day views
              - @mui/x-date-pickers: Material-UI calendar components
              - Custom calendar grid component
            - Features to include:
              - Month view with event dots/badges
              - Click event to see details and create bot
              - Visual indicators for events with active bots
              - Date range navigation
            - Consider complexity vs value - enhanced list with filters might be sufficient
            - User will revisit this based on actual usage patterns
            */}

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Calendar Events</Typography>
              <Button
                startIcon={<RefreshIcon />}
                onClick={loadCalendarEvents}
                disabled={loading}
              >
                Refresh
              </Button>
            </Stack>

            {loading && <CircularProgress />}

            {calendarEvents.length === 0 && !loading && (
              <Alert severity="info">
                No calendar events found. Connect your calendar provider first.
              </Alert>
            )}

            <List>
              {calendarEvents.map((event) => (
                <ListItem key={event.id} divider>
                  <ListItemText
                    primary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography>{event.title}</Typography>
                        {event.meetingUrl && <VideoCallIcon color="primary" fontSize="small" />}
                        {event.isRecurring && (
                          <Chip
                            label="Recurring"
                            size="small"
                            icon={<RefreshIcon />}
                            variant="outlined"
                            color="primary"
                          />
                        )}
                      </Stack>
                    }
                    secondary={
                      <>
                        <Typography variant="body2">
                          {new Date(event.startTime).toLocaleString()} - {new Date(event.endTime).toLocaleString()}
                        </Typography>
                        {event.meetingUrl && (
                          <Typography variant="caption" color="textSecondary">
                            Meeting URL: {event.meetingUrl}
                          </Typography>
                        )}
                      </>
                    }
                  />
                  {event.meetingUrl && (
                    <ListItemSecondaryAction>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleCreateBotFromEvent(event)}
                        disabled={loading}
                      >
                        Create Bot
                      </Button>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Meeting Bots Tab */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Meeting Bots</Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  startIcon={<RefreshIcon />}
                  onClick={loadBots}
                  disabled={loading}
                >
                  Refresh
                </Button>
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={() => setCreateBotDialogOpen(true)}
                >
                  Create Bot
                </Button>
              </Stack>
            </Stack>

            {bots.length === 0 && (
              <Alert severity="info">
                No bots created yet. Create one manually or from a calendar event.
              </Alert>
            )}

            {/* Helper function for status badges */}
            {(() => {
              // Create status chip with appropriate color and label based on Recall.ai statuses
              const getStatusChip = (status: string) => {
                const statusConfig: Record<string, {
                  color: 'default' | 'primary' | 'success' | 'warning' | 'error',
                  label: string
                }> = {
                  // Pre-call statuses
                  'ready': { color: 'primary', label: 'Ready' },
                  'joining_call': { color: 'primary', label: 'Joining Call' },

                  // Active call statuses
                  'in_waiting_room': { color: 'warning', label: 'In Waiting Room' },
                  'in_call_not_recording': { color: 'success', label: 'In Call (Not Recording)' },
                  'recording_permission_allowed': { color: 'success', label: 'Recording Allowed' },
                  'recording_permission_denied': { color: 'warning', label: 'Recording Denied' },
                  'in_call_recording': { color: 'success', label: 'Recording' },

                  // Terminal statuses
                  'recording_done': { color: 'default', label: 'Recording Done' },
                  'call_ended': { color: 'default', label: 'Call Ended' },
                  'done': { color: 'default', label: 'Done' },
                  'fatal': { color: 'error', label: 'Failed' },
                  'media_expired': { color: 'warning', label: 'Media Expired' },
                  'analysis_done': { color: 'success', label: 'Analysis Done' },
                  'analysis_failed': { color: 'error', label: 'Analysis Failed' }
                };

                const config = statusConfig[status] || { color: 'default', label: status };
                return <Chip label={config.label} size="small" color={config.color} />;
              };

              // Store the function for use in the list below
              (window as any).__getStatusChip = getStatusChip;
              return null;
            })()}

            <List>
              {bots.map((bot) => (
                <ListItem key={bot.botId} divider>
                  <ListItemText
                    primary={bot.botName || bot.botId}
                    secondary={
                      <>
                        <Box sx={{ mb: 1 }}>
                          {(window as any).__getStatusChip && (window as any).__getStatusChip(bot.status)}
                        </Box>
                        <Typography variant="caption">{bot.meetingUrl}</Typography>
                        {bot.joinAt && (
                          <Typography variant="caption" display="block" sx={{ color: 'primary.main', mt: 0.5 }}>
                            Scheduled: {new Date(bot.joinAt).toLocaleString()}
                          </Typography>
                        )}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={1}>
                      {/* Refresh status button - for transient statuses */}
                      {(PRE_CALL_STATUSES.includes(bot.status) || ACTIVE_CALL_STATUSES.includes(bot.status)) && (
                        <IconButton
                          size="small"
                          onClick={() => refreshBotStatus(bot.botId)}
                          disabled={loading}
                          title="Refresh Bot Status"
                        >
                          <RefreshIcon />
                        </IconButton>
                      )}

                      {/* Edit button for pre-call bots (ready, joining_call) */}
                      {PRE_CALL_STATUSES.includes(bot.status) && (
                        <IconButton
                          size="small"
                          onClick={() => handleEditBot(bot)}
                          disabled={loading}
                          title="Edit Bot"
                        >
                          <EditIcon />
                        </IconButton>
                      )}

                      {/* Leave call button for active bots - tests the leave_call endpoint */}
                      {ACTIVE_CALL_STATUSES.includes(bot.status) && (
                        <IconButton
                          size="small"
                          onClick={() => handleLeaveCall(bot.botId)}
                          disabled={loading}
                          title="Leave Call (graceful exit, continues processing)"
                        >
                          <ExitToAppIcon />
                        </IconButton>
                      )}

                      {/* More options menu for completed bots */}
                      {['done', 'recording_done', 'analysis_done', 'call_ended'].includes(bot.status) && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, bot)}
                          disabled={loading}
                          title="More Options"
                        >
                          <MoreVertIcon />
                        </IconButton>
                      )}

                      {/* Cancel button for pre-call bots - tests the DELETE endpoint */}
                      {PRE_CALL_STATUSES.includes(bot.status) && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => cancelBot(bot.botId)}
                          disabled={loading}
                          title="Cancel Bot (completely destroys bot)"
                        >
                          <CancelIcon />
                        </IconButton>
                      )}

                      {/* Delete button only for terminal statuses */}
                      {TERMINAL_STATUSES.includes(bot.status) && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteBot(bot.botId)}
                          disabled={loading}
                          title="Delete Bot Record"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Create Bot Dialog */}
      <Dialog open={createBotDialogOpen} onClose={() => setCreateBotDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Meeting Bot</DialogTitle>
        <DialogContent>
          <TextField
            label="Meeting URL"
            fullWidth
            value={meetingUrl}
            onChange={(e) => setMeetingUrl(e.target.value)}
            placeholder="https://meet.google.com/xxx-yyyy-zzz"
            margin="normal"
            required
          />
          <TextField
            label="Bot Name (Optional)"
            fullWidth
            value={botName}
            onChange={(e) => setBotName(e.target.value)}
            placeholder="My Meeting Bot"
            margin="normal"
          />
          <TextField
            label="Scheduled Join Time (Optional)"
            fullWidth
            type="datetime-local"
            value={scheduledTime}
            onChange={(e) => setScheduledTime(e.target.value)}
            margin="normal"
            helperText="Leave empty for immediate join, or schedule at least 10 minutes in the future"
            InputLabelProps={{
              shrink: true,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateBotDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={createManualBot}
            variant="contained"
            disabled={!meetingUrl || loading}
          >
            {scheduledTime ? 'Schedule Bot' : 'Create Bot'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Recurring Event Dialog */}
      <Dialog open={recurringDialogOpen} onClose={() => setRecurringDialogOpen(false)}>
        <DialogTitle>Recurring Event</DialogTitle>
        <DialogContent>
          <Typography>
            This is a recurring event. Would you like to schedule a bot for the next occurrence too?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecurringDialogOpen(false)}>Cancel</Button>
          <Button onClick={() => createBotFromEvent(selectedRecurringEvent!, false)}>
            Just this meeting
          </Button>
          <Button onClick={() => createBotFromEvent(selectedRecurringEvent!, true)} variant="contained">
            Schedule next occurrence
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Bot Dialog */}
      <Dialog open={editBotDialogOpen} onClose={() => setEditBotDialogOpen(false)}>
        <DialogTitle>Edit Bot</DialogTitle>
        <DialogContent>
          <TextField
            label="Meeting URL"
            value={meetingUrl}
            onChange={(e) => setMeetingUrl(e.target.value)}
            fullWidth
            margin="normal"
            helperText="Zoom, Teams, or Google Meet URL"
          />
          <TextField
            label="Bot Name (Optional)"
            value={botName}
            onChange={(e) => setBotName(e.target.value)}
            fullWidth
            margin="normal"
            helperText="Display name for the bot in the meeting"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditBotDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveEditedBot} variant="contained" disabled={loading || !meetingUrl}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* More Options Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleMenuAction('transcript')}>
          <ListItemIcon>
            <TranscriptIcon fontSize="small" />
          </ListItemIcon>
          View Raw Transcript
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('recording')}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          View Recording
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction('summary')}>
          <ListItemIcon>
            <SummarizeIcon fontSize="small" />
          </ListItemIcon>
          View Workflow Summary
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default TestMeetingBot;